import {
  createEmojisGQLMutation,
  createFilesGQLMutation,
  createImageGQLMutation,
  embedGQLQuery,
  updateImageGQLMutation,
  updateNetworkMediaSettingsGQLMutation,
} from '../../graphql/index.js'
import { ClientFileErrorHandler } from '../../lib/index.js'
import {
  AttachmentUploadType,
  CreateEmojiInput,
  CreateFileInput,
  CreateImageInput,
  Embed,
  EmbedInput,
  Emoji,
  File,
  Image,
  MediaNetworkSettings,
  MutationUpdateImageArgs,
  MutationUpdateNetworkMediaSettingsArgs,
  SignedUrl,
  UploadImagesArgs,
} from '../../types/index.js'
import { GraphqlClient } from '../graphql.client.js'

export class MediaClient {
  private client: GraphqlClient

  constructor(client: GraphqlClient) {
    this.client = client
  }

  async embed(variables: EmbedInput): Promise<Embed> {
    const result = await this.client.authorizedRequest<{ embed: Embed }>({
      query: embedGQLQuery(),
      variables,
    })

    return result.embed
  }

  async updateImage(variables: MutationUpdateImageArgs): Promise<Image> {
    const result = await this.client.authorizedRequest<{ updateImage: Image }>({
      query: updateImageGQLMutation(),
      variables,
    })

    return result.updateImage
  }

  async createEmojis(input: CreateEmojiInput[]): Promise<Array<Emoji>> {
    type QueryResult = { createEmojis: Array<Emoji> }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: createEmojisGQLMutation(),
      variables: {
        input,
      },
    })

    return result?.createEmojis
  }

  async createImages(variables: CreateImageInput[]): Promise<Array<SignedUrl>> {
    type QueryResult = { createImages: Array<SignedUrl> }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: createImageGQLMutation(),
      variables: {
        input: variables,
      },
    })

    return result?.createImages
  }

  async createFiles(variables: CreateFileInput[]): Promise<Array<SignedUrl>> {
    type QueryResult = { createFiles: Array<SignedUrl> }

    const result = await this.client.authorizedRequest<QueryResult>({
      query: createFilesGQLMutation(),
      variables: {
        input: variables,
      },
    })

    return result?.createFiles
  }

  async uploadFiles(
    input: AttachmentUploadType,
    handleError?: ClientFileErrorHandler,
  ): Promise<File[]> {
    const signedUrls = await this.createFiles(
      input.map(({ file: _, ...input }) => input),
    )

    const output: File[] = []

    const promises = signedUrls.map((signedUrl, index) => {
      const { file, name, size, extension, contentType } = input[index]
      const formData = new FormData()
      const parsedFields = JSON.parse(signedUrl.fields)
      // The order of appended key-value into the formData matters.
      Object.entries(parsedFields).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
      formData.append('Content-Type', file.type || contentType)
      formData.append('file', file)

      return fetch(signedUrl.signedUrl, {
        method: 'POST',
        body: formData,
      })
        .then(() => {
          output.push({
            id: signedUrl.mediaId,
            url: signedUrl.mediaUrl,
            name,
            downloadUrl: signedUrl.mediaDownloadUrl,
            size,
            extension,
          })
        })
        .catch(e => {
          if (handleError) {
            handleError({
              fileName: name,
              message: e?.message,
            })
          } else {
            throw e
          }
        })
    })

    await Promise.all(promises)

    return output
  }

  async uploadImages(
    input: UploadImagesArgs[],
    handleError?: ClientFileErrorHandler,
  ): Promise<Array<Image>> {
    const output: Image[] = []
    const signedUrls = await this.createImages(
      input.map(({ file, ...rest }) => ({
        ...rest,
        contentType: file.type,
        name: file.name,
        size: file.size,
      })),
    )

    const promises = signedUrls.map((signedUrl, index) => {
      const { file, name } = input[index]
      const formData = new FormData()
      const parsedFields = JSON.parse(signedUrl.fields)
      // The order of appended key-value into the formData matters.
      Object.entries(parsedFields).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
      formData.append('Content-Type', file.type)
      formData.append('file', file)

      return fetch(signedUrl.signedUrl, {
        method: 'POST',
        body: formData,
      })
        .then(r => r.text())
        .then(() => {
          output.push({
            id: signedUrl.mediaId,
            url: signedUrl.mediaUrl,
            urls: signedUrl.urls,
            cropX: input[index].cropX,
            cropY: input[index].cropY,
            cropZoom: input[index].cropZoom,
            name: input[index].name,
            cropHeight: input[index].cropHeight,
            cropWidth: input[index].cropWidth,
            downloadUrl: signedUrl.mediaDownloadUrl,
          })
        })
        .catch(error => {
          if (handleError) {
            handleError({
              fileName: name,
              message: error?.message,
            })
          } else {
            throw error
          }
        })
    })

    await Promise.all(promises.filter(Boolean))

    return output
  }

  async updateNetworkSettings(
    variables: MutationUpdateNetworkMediaSettingsArgs,
    accessToken?: string,
  ): Promise<MediaNetworkSettings> {
    type QueryResult = { updateNetworkMediaSettings: MediaNetworkSettings }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: updateNetworkMediaSettingsGQLMutation(),
      variables,
      customToken: accessToken,
    })
    return result.updateNetworkMediaSettings
  }
}
